import { Slot } from '@radix-ui/react-slot';
import { type VariantProps, cva } from 'class-variance-authority';
import type * as React from 'react';

import { cn } from '../../../lib';

// Literal classes only: uniq scans folio's source for Tailwind class names.
const gapClasses = {
  0: 'gap-0',
  1: 'gap-1',
  2: 'gap-2',
  2.5: 'gap-2.5',
  3: 'gap-3',
  4: 'gap-4',
  6: 'gap-6',
  8: 'gap-8',
} as const;

export type FlexGap = keyof typeof gapClasses;

const flexVariants = cva('flex', {
  variants: {
    align: {
      start: 'items-start',
      center: 'items-center',
      end: 'items-end',
      stretch: 'items-stretch',
      baseline: 'items-baseline',
    },
    justify: {
      start: 'justify-start',
      center: 'justify-center',
      end: 'justify-end',
      between: 'justify-between',
    },
    wrap: {
      true: 'flex-wrap',
    },
  },
});

type FlexProps = React.ComponentProps<'div'> &
  VariantProps<typeof flexVariants> & {
    asChild?: boolean;
    gap?: FlexGap;
  };

function flexClassName(
  direction: 'flex-col' | 'flex-row',
  { align, justify, wrap, gap }: Pick<FlexProps, 'align' | 'justify' | 'wrap' | 'gap'>,
  className?: string,
) {
  return cn(
    flexVariants({ align, justify, wrap }),
    direction,
    gap !== undefined && gapClasses[gap],
    className,
  );
}

function Stack({ asChild, align, justify, wrap, gap = 4, className, ...props }: FlexProps) {
  const Component = asChild ? Slot : 'div';
  return (
    <Component
      className={flexClassName('flex-col', { align, justify, wrap, gap }, className)}
      {...props}
    />
  );
}

function Group({
  asChild,
  align = 'center',
  justify,
  wrap,
  gap = 4,
  className,
  ...props
}: FlexProps) {
  const Component = asChild ? Slot : 'div';
  return (
    <Component
      className={flexClassName('flex-row', { align, justify, wrap, gap }, className)}
      {...props}
    />
  );
}

function Center({
  asChild,
  className,
  ...props
}: React.ComponentProps<'div'> & { asChild?: boolean }) {
  const Component = asChild ? Slot : 'div';
  return <Component className={cn('flex items-center justify-center', className)} {...props} />;
}

export { Center, Group, Stack };
